import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Container, 
  Button, 
  Chip, 
  Rating, 
  Divider,
  Grid,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  LinearProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { 
  ArrowBack, 
  LocationOn, 
  Assignment, 
  Store, 
  People, 
  AccessTime, 
  Star,
  CheckCircle,
  Warning,
  TrendingUp
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

const CampaignDetailPage: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // 더미 캠페인 데이터
  const campaignData = {
    id: campaignId,
    title: location.state?.campaignTitle || '캠페인 제목',
    description: '이 캠페인에 대한 자세한 설명이 들어갑니다. 참여자들이 알아야 할 모든 정보를 포함합니다.',
    imageUrl: 'https://placehold.co/800x400?text=캠페인이미지',
    category: '뷰티',
    reward: 500,
    participants: 23,
    maxParticipants: 30,
    rating: 4.8,
    reviewCount: 156,
    status: 'active',
    deadline: '2024-02-28',
    requirements: [
      '20세 이상',
      '뷰티 관심자',
      '인스타그램 계정 보유',
      '리뷰 작성 경험'
    ],
    benefits: [
      '무료 상품 체험',
      '포인트 적립',
      '리뷰어 등급 상승',
      '추가 혜택 제공'
    ],
    process: [
      '신청서 작성',
      '선정자 발표',
      '상품 수령',
      '체험 후 리뷰 작성',
      '포인트 지급'
    ]
  };

  const handleApply = () => {
    const isLoggedIn = localStorage.getItem('user');
    
    if (!isLoggedIn) {
      setSnackbar({
        open: true,
        message: '로그인이 필요한 서비스입니다.',
        severity: 'error'
      });
      return;
    }

    setSnackbar({
      open: true,
      message: '캠페인 신청이 완료되었습니다!',
      severity: 'success'
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const progressPercentage = (campaignData.participants / campaignData.maxParticipants) * 100;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* 뒤로가기 버튼 */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3, textTransform: 'none' }}
      >
        뒤로가기
      </Button>

      <Grid container spacing={4}>
        {/* 왼쪽 컬럼 - 캠페인 정보 */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Chip 
                label={campaignData.category}
                color="primary"
                variant="outlined"
                sx={{ mr: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                마감일: {campaignData.deadline}
              </Typography>
            </Box>

            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              {campaignData.title}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Rating value={campaignData.rating} precision={0.1} readOnly />
              <Typography variant="body1" sx={{ ml: 1, fontWeight: 'bold' }}>
                {campaignData.rating} ({campaignData.reviewCount}개 리뷰)
              </Typography>
            </Box>

            <img 
              src={campaignData.imageUrl} 
              alt={campaignData.title}
              style={{ 
                width: '100%', 
                height: 'auto', 
                borderRadius: '8px',
                marginBottom: '20px'
              }}
            />

            <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
              {campaignData.description}
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* 참여 현황 */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                참여 현황
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <People sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1">
                  {campaignData.participants} / {campaignData.maxParticipants}명
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={progressPercentage} 
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {Math.round(progressPercentage)}% 완료
              </Typography>
            </Box>

            {/* 참여 조건 */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                참여 조건
              </Typography>
              <List dense>
                {campaignData.requirements.map((req, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={req} />
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* 참여 혜택 */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                참여 혜택
              </Typography>
              <List dense>
                {campaignData.benefits.map((benefit, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Star color="warning" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={benefit} />
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* 참여 과정 */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                참여 과정
              </Typography>
              <List dense>
                {campaignData.process.map((step, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          width: 24, 
                          height: 24, 
                          borderRadius: '50%', 
                          bgcolor: 'primary.main', 
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {index + 1}
                      </Typography>
                    </ListItemIcon>
                    <ListItemText primary={step} />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Paper>
        </Grid>

        {/* 오른쪽 컬럼 - 신청 카드 */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {campaignData.reward}P
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              참여 완료 시 지급되는 포인트
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                남은 자리
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {campaignData.maxParticipants - campaignData.participants}자리
              </Typography>
            </Box>

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleApply}
              sx={{
                py: 1.5,
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                borderRadius: 2,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                }
              }}
            >
              캠페인 신청하기
            </Button>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
              신청 후 선정자 발표를 기다려주세요
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CampaignDetailPage; 